/**
 * Launch the Nova One mobile app from a web page.
 *
 * Used by the global Open-App banner (see `src/components/share/OpenAppBanner.tsx`)
 * and any other surface that wants to bridge a mobile-web visitor into the
 * native app. The strategy is:
 *
 *   1. iOS / Android only — desktop is a no-op (banner already won't render
 *      there, but the util is safe to call from anywhere).
 *   2. Try the custom-scheme deep link (`novataskapp://tasks/{id}` in prod,
 *      `novataskappdev://...` in dev; bare scheme = app home). The target →
 *      path mapping lives in `buildAppPath` below, the target type in
 *      `src/utils/appDeepLinkTarget.ts`. If the app is installed and
 *      the scheme handler is registered, the OS swaps to it. In WeChat /
 *      QQ in-app browsers this triggers the system's "leaving WeChat"
 *      confirmation dialog; the user has to tap "allow" to proceed.
 *   3. Fall back to App Store / Play Store after ~2s if the page is still
 *      visible (i.e. the app didn't take focus, so it's probably not
 *      installed). `visibilitychange` cancels the fallback when the OS
 *      hands focus to the app.
 *
 * Universal Link / App Link claiming (the `https://webapp...` URL opening
 * directly in the app) is handled at the OS layer via the AASA / assetlinks
 * files served at `/.well-known/...` — we don't try to programmatically
 * trigger that from JS because most browsers (notably mobile Safari) only
 * honor universal-link claims for direct user gestures on real `<a href>`s,
 * not for `window.location` assignments.
 */

import type { AppDeepLinkTarget } from './appDeepLinkTarget';

// iOS: prod task app's App Store ID. We use the prod ID for both dev and
// prod modes intentionally — fallback only fires when the user has NEITHER
// dev nor prod app installed (a rare path; TestFlight installs of the dev
// build still get launched via the scheme, no fallback needed). In that
// rare path it's better to point them at the actually-published prod app
// than to a dead TestFlight link.
const APP_STORE_URL = 'https://apps.apple.com/app/id6756265551';

// Android Play Store uses the package name directly — no separate numeric
// store ID exists. Same package across dev/prod builds for fallback purposes.
const PLAY_STORE_URL =
    'https://play.google.com/store/apps/details?id=com.lancetop.novataskapp';

const SCHEME_PROD = 'novataskapp';
const SCHEME_DEV = 'novataskappdev';
// Locally built dev-client (mobile APP_KIND=local → bundle id *.local).
const SCHEME_LOCAL = 'novataskapplocal';

// Debug override: append `?app_scheme=local` (or dev/prod) to any page URL to
// force which app the Open-App banner launches — lets developers point a
// webapp-dev page at the locally installed dev-client without redeploying.
// Query-param only (no localStorage) so the override can't silently stick to
// a phone after a debugging session ends.
const SCHEME_OVERRIDES: Record<string, string> = {
    local: SCHEME_LOCAL,
    dev: SCHEME_DEV,
    prod: SCHEME_PROD,
};

const FALLBACK_DELAY_MS = 2000;
// Slack/short delay tolerance: if visibilitychange fired within this window
// before the timer fires, we know the OS handed focus elsewhere (likely the
// app), so suppress the store fallback.
const FOCUS_LOSS_GRACE_MS = 200;

/**
 * Map a deep-link target to the in-app route path (no scheme, no leading
 * slash). `null` / unknown → empty string = open the app's home screen.
 *
 * Each path is verified against main-mobile's expo-router tree — see the
 * table in `appDeepLinkTarget.ts`. Note `assets/{id}/page`: the mobile route
 * file really is `assets/[id]/page.tsx`, the trailing segment is required.
 */
function buildAppPath(target?: AppDeepLinkTarget | null): string {
    if (!target?.id) return '';
    const id = encodeURIComponent(target.id);
    switch (target.kind) {
        case 'task': {
            // TaskDetailScreen reads `tab` / `focusSupply` from its search
            // params — that's how My Pickup List lands the worker straight on
            // the Supplies tab with one material row expanded.
            const query = new URLSearchParams();
            if (target.tab) query.set('tab', target.tab);
            if (target.focusSupply) query.set('focusSupply', target.focusSupply);
            const qs = query.toString();
            return `tasks/${id}${qs ? `?${qs}` : ''}`;
        }
        case 'issue':
            return `issues/${id}`;
        case 'asset':
            return `assets/${id}/page`;
        case 'purchase':
            return `po-receiving/${id}`;
        default:
            return '';
    }
}

/**
 * UA detection extracted so banner visibility and launch eligibility share
 * one definition. iPad on iPadOS 13+ reports a Mac UA, but we deliberately
 * don't include that — Universal Links work fine on iPad and the banner is
 * primarily for handheld in-app browsers (WeChat, etc.) which always set a
 * mobile UA.
 */
export function isMobileUserAgent(
    ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
): boolean {
    return /iPhone|iPod|Android.+Mobile/.test(ua);
}

function isIOS(ua: string): boolean {
    return /iPhone|iPod/.test(ua);
}

/**
 * Pick the scheme based on Vite mode. `prod` mode targets the prod app;
 * everything else (dev/localdev) targets the dev app. This mirrors the
 * mobile-side `app.config.ts` logic where the dev scheme has the `dev`
 * suffix. Behavior is correct only when this module is bundled by Vite —
 * in tests we explicitly stub `import.meta.env`.
 */
function getScheme(): string {
    if (typeof window !== 'undefined') {
        const override = new URLSearchParams(window.location.search).get(
            'app_scheme',
        );
        if (override && SCHEME_OVERRIDES[override]) {
            return SCHEME_OVERRIDES[override];
        }
    }
    const mode = import.meta.env?.MODE;
    return mode === 'prod' ? SCHEME_PROD : SCHEME_DEV;
}

export function launchApp(target?: AppDeepLinkTarget | null): void {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return;
    }
    const ua = navigator.userAgent;
    if (!isMobileUserAgent(ua)) return;

    const scheme = getScheme();
    const schemeUrl = `${scheme}://${buildAppPath(target)}`;
    const fallbackUrl = isIOS(ua) ? APP_STORE_URL : PLAY_STORE_URL;

    let focusLostAt: number | null = null;
    const onVisibilityChange = () => {
        if (document.hidden) {
            focusLostAt = Date.now();
        }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Trigger the scheme. On iOS this either opens the app, opens a "would
    // you like to open Nova One?" prompt, or silently fails (no installed
    // handler). On Android same idea via intent: scheme.
    window.location.href = schemeUrl;

    // Fall back to the store after FALLBACK_DELAY_MS, unless the OS has
    // hidden us recently (i.e. the app took over).
    window.setTimeout(() => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        const recentlyLostFocus =
            focusLostAt !== null &&
            Date.now() - focusLostAt < FALLBACK_DELAY_MS + FOCUS_LOSS_GRACE_MS;
        if (recentlyLostFocus || document.hidden) return;
        window.location.href = fallbackUrl;
    }, FALLBACK_DELAY_MS);
}

// Exposed for tests — kept off the default export so production callers stay
// on the small public surface (`launchApp` + `isMobileUserAgent`).
export const __testing = {
    APP_STORE_URL,
    PLAY_STORE_URL,
    SCHEME_PROD,
    SCHEME_DEV,
    SCHEME_LOCAL,
    FALLBACK_DELAY_MS,
};
